import React, { useEffect, useState } from 'react';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as constants from 'Constants';
import httpService from 'services/httpService';
import { MACHINES, browserChoices, thisDevice } from './device';

const PRIORITIES = [
  ['P3', 'P3 - Question or minor issue'],
  ['P2', 'P2 - A feature is broken'],
  ['P1', 'P1 - Platform down or exam blocked'],
];

// The machine and browser this page is open on. They start selected; people
// can pick others when the problem happened somewhere else.
const DETECTED = thisDevice();

const EMPTY = {
  subject: '', description: '', priority: 'P3', issue_type: 'customer_request', course_id: '',
  device: DETECTED.machine, browser: DETECTED.browser,
};

export default function SupportPage() {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [courses, setCourses] = useState([]);
  const [fileKey, setFileKey] = useState(0);

  const loadTickets = async () => {
    try {
      const res = await httpService.get(`${constants.base_url}/support/tickets/`);
      setTickets(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      /* list is optional */
    }
  };

  const loadCourses = async () => {
    try {
      const res = await httpService.get(`${constants.base_url}/support/courses/`);
      setCourses(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      /* course list is optional */
    }
  };

  useEffect(() => { loadTickets(); loadCourses(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length > 3) toast.warn('Only the first 3 files will be attached.');
    const tooBig = picked.find((f) => f.size > 10 * 1024 * 1024);
    if (tooBig) toast.error(`${tooBig.name} is larger than 10 MB.`);
    setFiles(picked.filter((f) => f.size <= 10 * 1024 * 1024).slice(0, 3));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append('page_url', document.referrer || window.location.href);
    files.forEach((f) => fd.append('files', f));
    try {
      const res = await httpService.post(`${constants.base_url}/support/tickets/`, fd);
      toast.success(`Submitted. Your ticket number is #${res.data.number}. Replies will come by email.`);
      setForm(EMPTY);
      setFiles([]);
      setFileKey((k) => k + 1);
      loadTickets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit. Please try again later.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 760, padding: '24px 16px' }}>
      <h3>Report an issue</h3>
      <p className="text-muted">Tell us what went wrong. Our support team will reply by email.</p>

      <Form onSubmit={submit}>
        <Form.Group className="mb-3">
          <Form.Label>Subject</Form.Label>
          <Form.Control value={form.subject} onChange={set('subject')} maxLength={150} required />
        </Form.Group>

        <div className="row">
          <Form.Group className="mb-3 col-md-6">
            <Form.Label>Type</Form.Label>
            <Form.Control as="select" value={form.issue_type} onChange={set('issue_type')}>
              <option value="customer_request">Help or request</option>
              <option value="code_issue">Something is broken</option>
            </Form.Control>
          </Form.Group>
          <Form.Group className="mb-3 col-md-6">
            <Form.Label>Priority</Form.Label>
            <Form.Control as="select" value={form.priority} onChange={set('priority')}>
              {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Form.Control>
          </Form.Group>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Course (optional)</Form.Label>
          <Form.Control as="select" value={form.course_id} onChange={set('course_id')}>
            <option value="">Not related to a specific course</option>
            {courses.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.code ? `${c.code} - ${c.title}` : c.title}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control as="textarea" rows={6} value={form.description} onChange={set('description')}
                        maxLength={5000} required
                        placeholder="What did you do, what did you expect, and what happened instead?" />
        </Form.Group>

        <div className="row">
          <Form.Group className="mb-1 col-md-6" controlId="support-machine">
            <Form.Label>Machine</Form.Label>
            <Form.Control as="select" value={form.device} onChange={set('device')}>
              {MACHINES.map((m) => (
                <option key={m} value={m}>{m === DETECTED.machine ? `${m} (this device)` : m}</option>
              ))}
            </Form.Control>
          </Form.Group>
          <Form.Group className="mb-1 col-md-6" controlId="support-browser">
            <Form.Label>Browser</Form.Label>
            <Form.Control as="select" value={form.browser} onChange={set('browser')}>
              {browserChoices(DETECTED.browser).map((b) => (
                <option key={b} value={b}>{b === DETECTED.browser ? `${b} (this browser)` : b}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </div>
        <Form.Text className="text-muted mb-3 d-block">
          Filled in from the device you are using now. Change them if the problem happened on another
          device or browser.
        </Form.Text>

        <Form.Group className="mb-3">
          <Form.Label>Screenshots or files (max 3, 10 MB each)</Form.Label>
          <Form.Control key={fileKey} type="file" multiple accept=".png,.jpg,.jpeg,.pdf,.txt,.log" onChange={onFiles} />
        </Form.Group>

        <Button type="submit" disabled={busy}>
          {busy ? <><Spinner size="sm" animation="border" /> Submitting...</> : 'Submit'}
        </Button>
      </Form>

      <h5 className="mt-5">My tickets</h5>
      {tickets.length === 0 ? (
        <p className="text-muted">No tickets yet.</p>
      ) : (
        <Table size="sm" bordered hover>
          <thead>
            <tr><th>#</th><th>Subject</th><th>Priority</th><th>Status</th><th>Created</th></tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id}>
                <td>{t.number || '-'}</td>
                <td>{t.subject}</td>
                <td>{t.priority}</td>
                <td>{t.state}</td>
                <td>{new Date(t.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
